import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import EmptyState from "../components/common/EmptyState";
import Modal from "../components/common/Modal";
import { agentApi } from "../lib/apiClient";
import { showApiError, showSuccessAction, notifyFinanceUpdated } from "../lib/alerts";
import { asArray, downloadBlob, formatCurrency, formatDate, formatDateTime, unwrapData } from "../lib/catalog";
import { formatPaymentStatus } from "../lib/bookingStatus";
import { fetchCurrentAgent } from "../lib/agentAuth";
import { canWriteBookings, roleFromProfile } from "../lib/agencyAccess";

function Stat({ icon, label, value, tone = "primary" }) {
  return (
    <div className="col-6 col-xl-3">
      <div className="card border-0 shadow-sm h-100 radius-8">
        <div className="card-body p-24">
          <div className="d-flex align-items-center justify-content-between gap-3">
            <div className="min-w-0">
              <span className="mb-8 fw-medium text-secondary-light text-sm">{label}</span>
              <h6 className="fw-semibold mb-0 text-break">{value}</h6>
            </div>
            <div className={`w-50-px h-50-px bg-${tone} rounded-circle d-flex justify-content-center align-items-center flex-shrink-0`}>
              <iconify-icon icon={icon} className="text-white text-2xl mb-0"></iconify-icon>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WalletPage() {
  const [outstanding, setOutstanding] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txSummary, setTxSummary] = useState({});
  const [commissions, setCommissions] = useState([]);
  const [commissionSummary, setCommissionSummary] = useState({});
  const [account, setAccount] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingId, setPayingId] = useState("");
  const [walletPayTarget, setWalletPayTarget] = useState(null);
  const [canCreateBooking, setCanCreateBooking] = useState(false);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [outstandingPayload, txPayload, commissionPayload, accountPayload, ledgerPayload, schedulePayload, profile] = await Promise.all([
          agentApi.get("/payments/outstanding"),
          agentApi.get("/travel-partner/transactions", { params: { limit: 50, page: 1 } }),
          agentApi.get("/travel-partner/commissions", { params: { limit: 50, page: 1 } }),
          agentApi.get("/travel-partner/finance/account").catch(() => null),
          agentApi.get("/travel-partner/finance/ledger", { params: { limit: 50, page: 1 } }).catch(() => null),
          agentApi.get("/travel-partner/finance/schedules").catch(() => null),
          fetchCurrentAgent().catch(() => null),
        ]);
        if (!alive) return;
        setCanCreateBooking(canWriteBookings(roleFromProfile(profile)));
        const outstandingData = unwrapData(outstandingPayload);
        const txData = unwrapData(txPayload);
        const commissionData = unwrapData(commissionPayload);
        const accountData = unwrapData(accountPayload);
        const ledgerData = unwrapData(ledgerPayload);
        const scheduleData = unwrapData(schedulePayload);
        setOutstanding(asArray(outstandingData));
        setTransactions(asArray(txData));
        setTxSummary(txData.summary || {});
        setCommissions(asArray(commissionData));
        setCommissionSummary(commissionData.summary || {});
        setAccount(accountData && typeof accountData === "object" ? accountData : null);
        setLedger(asArray(ledgerData?.data || ledgerData));
        setSchedules(asArray(scheduleData));
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_WALLET");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  const outstandingRows = outstanding.filter((row) => Number(row.outstanding || 0) > 0);
  const outstandingTotal = outstandingRows.reduce((sum, row) => sum + Number(row.outstanding || 0), 0);

  async function downloadReceipt(paymentId) {
    try {
      const blob = await agentApi.get(`/payments/${paymentId}/receipt`, { responseType: "blob" });
      downloadBlob(blob, `receipt-${paymentId}`);
      showSuccessAction("DOWNLOAD_RECEIPT");
    } catch (downloadError) {
      showApiError(downloadError, "DOWNLOAD_RECEIPT");
    }
  }

  async function requestRefund(paymentId) {
    const reason = window.prompt("Refund request reason");
    if (!reason) return;
    try {
      await agentApi.post(`/payments/${paymentId}/refund-request`, { body: { reason } });
      showSuccessAction("REQUEST_REFUND");
    } catch (refundError) {
      showApiError(refundError, "REQUEST_REFUND");
    }
  }

  const walletBalance = Number(account?.wallet_balance || 0);

  function openWalletPay(row) {
    const amount = Number(row.outstanding || 0);
    if (amount <= 0) return;
    if (walletBalance < amount) {
      window.location.assign(`/payment-proof?bookingId=${row.booking_id}&method=wallet`);
      return;
    }
    setWalletPayTarget(row);
  }

  function closeWalletPay() {
    if (payingId) return;
    setWalletPayTarget(null);
  }

  async function confirmWalletPay() {
    const row = walletPayTarget;
    const amount = Number(row?.outstanding || 0);
    if (!row || amount <= 0) return;
    try {
      setPayingId(String(row.booking_id));
      await agentApi.post(`/payments/booking/${row.booking_id}/wallet`, { body: { amount } });
      showSuccessAction("PAY_FROM_WALLET");
      notifyFinanceUpdated();
      setWalletPayTarget(null);
      const [outstandingPayload, accountPayload, ledgerPayload, schedulePayload] = await Promise.all([
        agentApi.get("/payments/outstanding"),
        agentApi.get("/travel-partner/finance/account").catch(() => null),
        agentApi.get("/travel-partner/finance/ledger", { params: { limit: 50, page: 1 } }).catch(() => null),
        agentApi.get("/travel-partner/finance/schedules").catch(() => null),
      ]);
      setOutstanding(asArray(unwrapData(outstandingPayload)));
      const accountData = unwrapData(accountPayload);
      setAccount(accountData && typeof accountData === "object" ? accountData : account);
      const ledgerData = unwrapData(ledgerPayload);
      setLedger(asArray(ledgerData?.data || ledgerData));
      setSchedules(asArray(unwrapData(schedulePayload)));
    } catch (payError) {
      showApiError(payError, "PAY_FROM_WALLET");
    } finally {
      setPayingId("");
    }
  }

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Outstanding"
          title="Collections, outstanding and commissions"
          items={[DASHBOARD_CRUMB, { label: "Outstanding" }]}
          actions={
            <div className="page-toolbar">
              {canCreateBooking ? (
                <Link to="/packages" className="btn btn-outline-primary">
                  <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                  Add booking
                </Link>
              ) : null}
              <Link to="/payment-proof" className="btn btn-primary">
                <iconify-icon icon="solar:upload-minimalistic-outline"></iconify-icon>
                Upload payment proof
              </Link>
            </div>
          }
        />

        {loading ? <PageLoader message="Loading outstanding balances..." /> : null}

        {!loading ? (
          <>
            {account?.booking_blocked ? (
              <div className="alert alert-danger" role="alert">
                {account.overdue_blocked
                  ? "New bookings are blocked because a scheduled collection is overdue."
                  : "New bookings are blocked because available credit is exhausted."}
              </div>
            ) : null}
            <div className="row g-4 mb-4">
              <Stat icon="solar:clock-circle-outline" label="Outstanding" value={formatCurrency(account?.outstanding ?? outstandingTotal)} tone="warning" />
              <Stat icon="solar:wallet-money-outline" label="Wallet balance" value={formatCurrency(account?.wallet_balance ?? txSummary.success_amount)} tone="success" />
              <Stat icon="solar:card-outline" label="Credit limit" value={formatCurrency(account?.credit_limit)} tone="info" />
              <Stat icon="solar:check-circle-outline" label="Available credit" value={formatCurrency(account?.available_credit)} />
            </div>
            <p className="text-secondary text-sm mb-4">
              Credit limit is the amount this agency can keep unpaid. Available credit is credit limit + wallet − outstanding.
              Pay from wallet to clear a booking immediately. Bank / UPI / cash still need a proof upload for admin approval.
              Overdue scheduled collections also block new bookings.
            </p>

            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-24">
                <h5 className="mb-3">Outstanding bookings</h5>
                {outstandingRows.length > 0 ? (
                  <div className="table-responsive">
                  <Table>
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Package</th>
                        <th className="text-end">Booking total</th>
                        <th className="text-end">Paid</th>
                        <th className="text-end">Outstanding</th>
                        <th>Payment status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingRows.map((row) => (
                        <tr key={row.booking_id}>
                          <td>
                            <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
                              {row.booking_reference}
                            </Link>
                          </td>
                          <td className="text-break">{row.package_name || "-"}</td>
                          <td className="text-end">{formatCurrency(row.booking_total)}</td>
                          <td className="text-end">{formatCurrency(row.successful_payments)}</td>
                          <td className="text-end">{formatCurrency(row.outstanding)}</td>
                          <td><StatusBadge value={row.payment_status || "pending"}>{formatPaymentStatus(row.payment_status)}</StatusBadge></td>
                          <td className="text-end">
                            <div className="d-inline-flex flex-wrap justify-content-end gap-2">
                              {walletBalance > 0 ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  disabled={payingId === String(row.booking_id)}
                                  onClick={() => openWalletPay(row)}
                                >
                                  {payingId === String(row.booking_id)
                                    ? "Paying..."
                                    : walletBalance >= Number(row.outstanding || 0)
                                      ? "Pay from wallet"
                                      : "Partial from wallet"}
                                </button>
                              ) : null}
                              <Link to={`/payment-proof?bookingId=${row.booking_id}`} className="btn btn-sm btn-outline-primary">
                                Upload proof
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  </div>
                ) : (
                  <EmptyState
                    variant="outstanding"
                    title="No outstanding bookings."
                    description="Create a booking or upload a payment proof when collections are due."
                    action={(
                      <div className="d-flex flex-wrap justify-content-center gap-2">
                        {canCreateBooking ? (
                          <Link to="/packages" className="btn btn-outline-primary">
                            <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                            Add booking
                          </Link>
                        ) : null}
                        <Link to="/payment-proof" className="btn btn-primary">
                          <iconify-icon icon="solar:upload-minimalistic-outline"></iconify-icon>
                          Upload payment proof
                        </Link>
                      </div>
                    )}
                  />
                )}
              </div>
            </div>

            {schedules.length > 0 ? (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-24">
                <h5 className="mb-3">Payment schedules</h5>
                <div className="table-responsive">
                  <Table>
                    <thead>
                      <tr>
                        <th>Booking</th>
                        <th>Due date & time</th>
                        <th className="text-end">Amount</th>
                        <th>Status</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schedules.map((row) => (
                        <tr key={row.schedule_id}>
                          <td>
                            {row.booking_id ? (
                              <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
                                {row.booking_reference || row.booking_id}
                              </Link>
                            ) : "-"}
                          </td>
                          <td>{row.due_date_label || formatDateTime(row.due_at || row.due_date)}</td>
                          <td className="text-end">{formatCurrency(row.amount)}</td>
                          <td><StatusBadge value={row.status || "pending"}>{String(row.status || "pending")}</StatusBadge></td>
                          <td className="text-end">
                            {["pending", "overdue"].includes(String(row.status || "").toLowerCase()) ? (
                              <Link
                                to={`/payment-proof?bookingId=${row.booking_id}&method=wallet`}
                                className="btn btn-sm btn-outline-primary"
                              >
                                Pay now
                              </Link>
                            ) : "-"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
            ) : null}

            {ledger.length > 0 ? (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-24">
                <h5 className="mb-3">Wallet ledger</h5>
                <div className="table-responsive">
                  <Table>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Kind</th>
                        <th>Applied to</th>
                        <th className="text-end">Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledger.map((row) => (
                        <tr key={row.transaction_id}>
                          <td>{formatDate(row.created_date)}</td>
                          <td>{String(row.transaction_kind || "").replace(/_/g, " ") || "-"}</td>
                          <td>{row.applied_to || "booking"}</td>
                          <td className="text-end">{formatCurrency(row.amount)}</td>
                          <td><StatusBadge value={row.status || "pending"}>{formatPaymentStatus(row.status)}</StatusBadge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
            ) : null}

            <div className="row g-4">
              <div className="col-12 col-xl-7">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-24">
                    <h5 className="mb-3">Transactions</h5>
                    {transactions.length > 0 ? (
                      <Table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Reference</th>
                            <th>Type</th>
                            <th className="text-end">Amount</th>
                            <th>Status</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((row) => (
                            <tr key={row.transaction_id}>
                              <td>{formatDate(row.transaction_date)}</td>
                              <td className="text-break">
                                {row.booking_id ? (
                                  <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
                                    {row.booking_reference || row.reference_number}
                                  </Link>
                                ) : (
                                  row.booking_reference || row.reference_number || "-"
                                )}
                              </td>
                              <td>{String(row.transaction_kind || "payment") === "refund" ? "Debit" : "Credit"}</td>
                              <td className="text-end">{formatCurrency(row.amount)}</td>
                              <td><StatusBadge value={row.status || "pending"}>{formatPaymentStatus(row.status)}</StatusBadge></td>
                              <td className="text-end">
                                <div className="d-inline-flex align-items-center justify-content-end gap-2">
                                  <button
                                    type="button"
                                    className="agent-icon-btn"
                                    title="Receipt"
                                    aria-label="Download receipt"
                                    onClick={() => downloadReceipt(row.transaction_id)}
                                  >
                                    <iconify-icon icon="solar:download-minimalistic-outline"></iconify-icon>
                                  </button>
                                  {String(row.transaction_kind || "payment") !== "refund" ? (
                                    <button
                                      type="button"
                                      className="agent-icon-btn"
                                      title="Refund"
                                      aria-label="Request refund"
                                      onClick={() => requestRefund(row.transaction_id)}
                                    >
                                      <iconify-icon icon="solar:restart-outline"></iconify-icon>
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <EmptyState
                        variant="outstanding"
                        title="No transactions yet."
                        description="Upload a payment proof against a booking to record a collection."
                        action={(
                          <Link to="/payment-proof" className="btn btn-primary">
                            <iconify-icon icon="solar:upload-minimalistic-outline"></iconify-icon>
                            Upload payment proof
                          </Link>
                        )}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-5">
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body p-24">
                    <h5 className="mb-3">Commissions</h5>
                    {commissions.length > 0 ? (
                      <Table>
                        <thead>
                          <tr>
                            <th>Booking</th>
                            <th className="text-end">Amount</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {commissions.map((row) => (
                            <tr key={row.commission_id}>
                              <td>
                                {row.booking_id ? (
                                  <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
                                    {row.booking_reference || row.booking_id}
                                  </Link>
                                ) : (
                                  row.booking_id
                                )}
                              </td>
                              <td className="text-end">{formatCurrency(row.commission_amount)}</td>
                              <td><StatusBadge value={row.commission_status || "-"} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <EmptyState
                        variant="outstanding"
                        title="No commissions yet."
                        description="Commissions appear after bookings are collected."
                        action={canCreateBooking ? (
                          <Link to="/packages" className="btn btn-primary">
                            <iconify-icon icon="solar:add-circle-outline"></iconify-icon>
                            Add booking
                          </Link>
                        ) : undefined}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <Modal
        open={Boolean(walletPayTarget)}
        title="Pay from wallet"
        onClose={closeWalletPay}
        footer={(
          <>
            <button type="button" className="btn btn-outline-secondary" disabled={Boolean(payingId)} onClick={closeWalletPay}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" disabled={Boolean(payingId)} onClick={confirmWalletPay}>
              {payingId ? "Paying..." : "Pay from wallet"}
            </button>
          </>
        )}
      >
        <p className="mb-3">
          Pay <strong>{formatCurrency(walletPayTarget?.outstanding)}</strong> from wallet for{" "}
          <strong>{walletPayTarget?.booking_reference}</strong>? This is applied immediately.
        </p>
        <div className="small bg-neutral-100 rounded-3 p-3">
          <div className="d-flex justify-content-between gap-3 py-1">
            <span>Wallet balance</span>
            <span className="fw-semibold">{formatCurrency(walletBalance)}</span>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1">
            <span>Amount to pay</span>
            <span className="fw-semibold">{formatCurrency(walletPayTarget?.outstanding)}</span>
          </div>
          <div className="d-flex justify-content-between gap-3 py-1 border-top mt-2 pt-2">
            <span>Balance after payment</span>
            <span className="fw-semibold">{formatCurrency(walletBalance - Number(walletPayTarget?.outstanding || 0))}</span>
          </div>
        </div>
      </Modal>
    </AgentLayout>
  );
}
